import { useEffect, useMemo, useState } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import {
  Maximize2,
  Wallet,
  Users,
  X,
  Layers,
  Focus,
  ArrowLeftRight,
  Check,
  AlertTriangle,
} from "lucide-react";

import { FormField, inputCls, readOnlyCls } from "../../../components";
import {
  buildProfileMap,
  calcStockPartsFromPortfolios,
} from "../suitability/SuitabilityForm.rules";
import type { PendingOrder } from "../suitability/SuitabilityForm.types";
import type { QueryModelPosition, QueryProfile } from "@/api/queries/types";
import { useDownloadReport } from "@/api/reports/useDownloadReport";
import type { Contact, Portfolio } from "@/api/types";
import { Button } from "@/components/Button/Button";
import { AutoSuit } from "@/forms/classes/AutoSuit";
import { cn } from "@/lib/utils";

export type SuitabilityNewFormProps = {
  contact: Contact;
  profileData: QueryProfile[];
  portfolios: Portfolio[];
  pendingOrders: PendingOrder[];
  modelsData: QueryModelPosition[];
  paramOverrides?: Record<string, string>;
  onDocumentCreated?: (data: Record<string, unknown>) => void;
  onClose?: () => void;
};

function pct(value: number | null | undefined, decimals = 1): string {
  if (value == null) return "—";
  return `${(value * 100).toFixed(decimals)} %`;
}

function fmtNok(value: number | null | undefined): string {
  if (value == null) return "—";
  return value.toLocaleString("nb-NO", { maximumFractionDigits: 0 });
}

function todayForDatePicker(): Date {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate());
}

function formatDateForPayload(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

// Scope B: raw user input for financial investments held outside Capguard,
// plus the derived total/object AutoSuit needs. Kept as form-local state since
// it's tied to this form's inputs — AutoSuit only ever receives the derived object.
function useScopeBInvestments() {
  const [scopeBEquity, setScopeBEquity] = useState<number | "">(0);
  const [scopeBInterest, setScopeBInterest] = useState<number | "">(0);
  const [scopeBCash, setScopeBCash] = useState<number | "">(0);
  const [scopeBOther, setScopeBOther] = useState<number | "">(0);
  const [scopeBDate, setScopeBDate] = useState(todayForDatePicker);

  const scopeBTotal =
    (Number(scopeBEquity) || 0) +
    (Number(scopeBInterest) || 0) +
    (Number(scopeBCash) || 0) +
    (Number(scopeBOther) || 0);

  const asScopeB = useMemo(
    () => ({
      equity: Number(scopeBEquity) || 0,
      interest: Number(scopeBInterest) || 0,
      cash: Number(scopeBCash) || 0,
      other: Number(scopeBOther) || 0,
    }),
    [scopeBEquity, scopeBInterest, scopeBCash, scopeBOther]
  );

  return {
    scopeBEquity,
    setScopeBEquity,
    scopeBInterest,
    setScopeBInterest,
    scopeBCash,
    setScopeBCash,
    scopeBOther,
    setScopeBOther,
    scopeBDate,
    setScopeBDate,
    scopeBTotal,
    asScopeB,
  };
}

export function SuitabilityNewForm({
  contact,
  profileData,
  portfolios,
  pendingOrders,
  modelsData,
  paramOverrides = {},
  onDocumentCreated,
  onClose,
}: SuitabilityNewFormProps) {
  const [scope, setScope] = useState<"A" | "B">("A");
  const [additionalInformationText, setAdditionalInformationText] =
    useState("");

  // Scope B: financial investments outside Capguard — needed by AutoSuit for
  // recommendationShareAfter / overallStockPartBefore / overallStockPartAfter.
  const {
    scopeBEquity,
    setScopeBEquity,
    scopeBInterest,
    setScopeBInterest,
    scopeBCash,
    setScopeBCash,
    scopeBOther,
    setScopeBOther,
    scopeBDate,
    setScopeBDate,
    scopeBTotal,
    asScopeB,
  } = useScopeBInvestments();

  const [manuellA, setManuellA] = useState(false);
  const [manuellB, setManuellB] = useState(false);
  const [manuellSubChoice, setManuellSubChoice] = useState<
    "temporary" | "outside" | "pension" | "custom" | null
  >(null);
  const [manuellCustomText, setManuellCustomText] = useState("");
  const activeManuell = scope === "A" ? manuellA : manuellB;

  const { downloadReport, downloading } = useDownloadReport(
    `Capguard_Suitability_ORG_${new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[:T]/g, "-")}.pdf`,
    false
  );
  const { downloadReport: downloadReportPriv, downloading: downloadingPriv } =
    useDownloadReport(
      `Capguard_Suitability_PRIV_${new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:T]/g, "-")}.pdf`,
      false
    );

  const baseProfileMap = useMemo(
    () => buildProfileMap(profileData),
    [profileData]
  );

  const stockParts = useMemo(
    () => calcStockPartsFromPortfolios(portfolios, pendingOrders, modelsData),
    [portfolios, pendingOrders, modelsData]
  );

  const data = useMemo(() => {
    const suit = new AutoSuit(
      baseProfileMap,
      {
        ...paramOverrides,
        _stockCurrent: String(stockParts.current),
        _stockFin: String(stockParts.fin),
        _investmentAmount: String(
          pendingOrders.reduce(
            (sum, o) => sum + (o.amountToInvest ?? 0) + (o.oneOffAmount ?? 0),
            0
          )
        ),
      },
      contact,
      [],
      pendingOrders,
      modelsData,
      scope,
      activeManuell,
      asScopeB,
      portfolios
    );
    return suit.toData();
  }, [
    baseProfileMap,
    stockParts,
    contact,
    pendingOrders,
    modelsData,
    paramOverrides,
    scope,
    activeManuell,
    asScopeB,
    portfolios,
  ]);

  const [realisasjon, setRealisasjon] = useState(false);
  const [realisasjonEksisterende, setRealisasjonEksisterende] = useState("");
  const [realisasjonAnbefalt, setRealisasjonAnbefalt] = useState("");
  const [realisasjonKostnader, setRealisasjonKostnader] = useState("");
  const [realisasjonFordeler, setRealisasjonFordeler] = useState("");
  const [realisasjonBegrunnelse, setRealisasjonBegrunnelse] = useState("");

  const [investmentVsNetWorthComment, setInvestmentVsNetWorthComment] =
    useState("");
  const [sustainabilityMatchDescription, setSustainabilityMatchDescription] =
    useState("");

  const hasEsgPreference = !!data.esgPreference && data.esgPreference !== "Nei";

  const manuellData = useMemo(
    () =>
      activeManuell
        ? {
            manuell: true,
            temporaryPlacement: manuellSubChoice === "temporary",
            fundsOutsideLongTermAllocation: manuellSubChoice === "outside",
            manuellSubChoice,
            manuellCustomText:
              manuellSubChoice === "custom" ? manuellCustomText || null : null,
          }
        : { manuell: false },
    [activeManuell, manuellSubChoice, manuellCustomText]
  );

  const resetManuellSubFields = () => {
    setManuellSubChoice(null);
    setManuellCustomText("");
  };

  // A særskilt begrunnelse is only offered when AutoSuit flags the case as needing one.
  const manualSuitabilityRequired =
    data.suitabilityStatus === "MANUAL_SUITABILITY_REQUIRED";

  // "Midlertidig plassering" / "Utenfor langsiktig allokering" only apply when the
  // relevant equity share is below the recommended range, not above it.
  const isBelowRecommendedRange =
    data.recommendedMin == null
      ? false
      : scope === "A"
      ? data.equityShareInOrders != null &&
        data.equityShareInOrders < data.recommendedMin
      : data.scopeBEquityShareAfter != null &&
        data.scopeBEquityShareAfter < data.recommendedMin;

  // Reset all manuell state when scope changes.
  useEffect(() => {
    setManuellA(false);
    setManuellB(false);
    resetManuellSubFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // Drop any manuell input once the case no longer requires a særskilt begrunnelse.
  useEffect(() => {
    if (manualSuitabilityRequired) return;
    setManuellA(false);
    setManuellB(false);
    resetManuellSubFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualSuitabilityRequired]);

  // Clear temporary/outside once the case is no longer below the recommended range.
  useEffect(() => {
    if (isBelowRecommendedRange) return;
    if (manuellSubChoice === "temporary" || manuellSubChoice === "outside") {
      resetManuellSubFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBelowRecommendedRange]);

  const hasOrders = pendingOrders.length > 0;

  // Auto-select scope based on whether order equity allocation is within the recommended band.
  useEffect(() => {
    if (data.ordersInsideRecommendedRange === true) setScope("A");
    else if (data.ordersInsideRecommendedRange === false) setScope("B");
  }, [data.ordersInsideRecommendedRange]);

  const scopeBData = useMemo(() => {
    if (scope !== "B") return {};

    const toNumberOrNull = (value: number | "") =>
      value === "" ? null : Number(value);

    const hasAnyInput =
      scopeBEquity !== "" ||
      scopeBInterest !== "" ||
      scopeBCash !== "" ||
      scopeBOther !== "";

    return {
      finInvUtenforCapguardAksjer: toNumberOrNull(scopeBEquity),
      finInvUtenforCapguardRenter: toNumberOrNull(scopeBInterest),
      finInvUtenforCapguardKontanter: toNumberOrNull(scopeBCash),
      finInvUtenforCapguardOvrige: toNumberOrNull(scopeBOther),
      finInvUtenforCapguardTotalt:
        hasAnyInput || scopeBTotal !== 0 ? scopeBTotal : null,
      finInvUtenforCapguardDato: dayjs(scopeBDate).format("DD.MM.YYYY"),
    };
  }, [
    scope,
    scopeBEquity,
    scopeBInterest,
    scopeBCash,
    scopeBOther,
    scopeBDate,
    scopeBTotal,
  ]);

  const realisasjonData = useMemo(
    () =>
      realisasjon
        ? {
            realisasjon: true,
            realisasjonEksisterende: realisasjonEksisterende || null,
            realisasjonAnbefalt: realisasjonAnbefalt || null,
            realisasjonKostnader: realisasjonKostnader || null,
            realisasjonFordeler: realisasjonFordeler || null,
            realisasjonBegrunnelse: realisasjonBegrunnelse || null,
          }
        : { realisasjon: false },
    [
      realisasjon,
      realisasjonEksisterende,
      realisasjonAnbefalt,
      realisasjonKostnader,
      realisasjonFordeler,
      realisasjonBegrunnelse,
    ]
  );

  const realisasjonComplete =
    !realisasjon ||
    (!!realisasjonEksisterende &&
      !!realisasjonAnbefalt &&
      !!realisasjonKostnader &&
      !!realisasjonFordeler &&
      !!realisasjonBegrunnelse);
  const realisasjonCompletedFields = [
    realisasjonEksisterende,
    realisasjonAnbefalt,
    realisasjonKostnader,
    realisasjonFordeler,
    realisasjonBegrunnelse,
  ].filter((value) => value.trim()).length;

  const investmentVsNetWorthComplete =
    !data.investmentvsNetWorthFlag || !!investmentVsNetWorthComment;

  const manuellCustomComplete =
    !activeManuell ||
    manuellSubChoice !== "custom" ||
    !!manuellCustomText.trim();

  const sustainabilityComplete =
    !hasEsgPreference || !!sustainabilityMatchDescription.trim();

  // Scope A requires Manuell when orders are outside the recommended equity band
  const scopeAManuellRequired =
    scope === "A" && data.ordersInsideRecommendedRange === false && !manuellA;

  // Scope B is only meaningful with holdings outside Capguard — otherwise Scope A applies.
  const scopeBRequiresInvestments = scope === "B" && scopeBTotal <= 0;

  // Consolidated output — everything that goes into the produced document,
  // including which advice scope (A/B) and manual-override variant was chosen.
  const finalData = useMemo(
    () => ({
      ...data,
      scope,
      adviceScope: scope,
      manual: activeManuell,
      manualVariant: activeManuell ? manuellSubChoice : null,
      manualCustomText:
        activeManuell && manuellSubChoice === "custom"
          ? manuellCustomText || null
          : null,
      additionalInformationText,
      ...scopeBData,
      ...manuellData,
      ...realisasjonData,
      investmentVsNetWorthFlag: data.investmentvsNetWorthFlag,
      investmentVsNetWorthComment: data.investmentvsNetWorthFlag
        ? investmentVsNetWorthComment || null
        : null,
      sustainabilityMatchDescription: hasEsgPreference
        ? sustainabilityMatchDescription || null
        : null,
    }),
    [
      data,
      scope,
      activeManuell,
      manuellSubChoice,
      manuellCustomText,
      additionalInformationText,
      scopeBData,
      manuellData,
      realisasjonData,
      investmentVsNetWorthComment,
      hasEsgPreference,
      sustainabilityMatchDescription,
    ]
  );

  const handleDownloadReportPriv = async () => {
    await downloadReportPriv("Capguard - Suitability PRIV", {
      data: finalData,
    });
  };
  const handleDownloadReport = async () => {
    await downloadReport("Capguard - Suitability ORG", {
      data: finalData,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onDocumentCreated?.(finalData);
    onClose?.();
  };

  return (
    <form onSubmit={handleSubmit} className="flex gap-5 items-start">
      {/* ── Main column ─────────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 space-y-5">
        {/* Header with live suitability status */}
        <div className="flex items-center justify-between gap-3 pb-3 border-b border-border/40">
          <h2 className="text-base font-semibold">Egnethetsvurdering</h2>
          <SuitabilityStatusBadge status={data.suitabilityStatus} />
        </div>

        {/* No orders */}
        {!hasOrders && (
          <div
            className="flex items-start gap-3 rounded-xl border border-amber-300/70 bg-amber-50/80 px-3.5 py-3 text-amber-950 shadow-sm dark:border-amber-700/70 dark:bg-amber-950/30 dark:text-amber-100"
            role="alert"
          >
            <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600 dark:bg-amber-900/60 dark:text-amber-400">
              <AlertTriangle className="size-4" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1 space-y-0.5">
              <p className="text-sm font-semibold leading-snug">
                No orders have been added
              </p>
              <p className="text-xs leading-relaxed text-amber-800/90 dark:text-amber-200/90">
                Add at least one order to create a suitability assessment.
              </p>
            </div>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 rounded-md border border-amber-300 px-3 py-1.5 text-xs font-medium text-amber-900 transition-colors hover:bg-amber-100 dark:border-amber-600 dark:text-amber-100 dark:hover:bg-amber-900/30"
              >
                Avbryt
              </button>
            )}
          </div>
        )}

        {hasOrders && (
          <>
            {/* ── Scope ──────────────────────────────────────────────────── */}
            <section aria-labelledby="recommendation-scope-heading">
              <div className="mb-2.5">
                <h3
                  id="recommendation-scope-heading"
                  className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground"
                >
                  Anbefalingens omfang
                </h3>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  Velg hvordan investeringen skal vurderes.
                </p>
              </div>
              <div
                className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2"
                role="radiogroup"
                aria-label="Anbefalingens omfang"
              >
                {/* Scope A */}
                <div
                  onClick={() => setScope("A")}
                  className={cn(
                    "group relative flex cursor-pointer flex-col rounded-xl border p-5 text-left transition-all duration-200 focus-within:ring-2 focus-within:ring-emerald-500 focus-within:ring-offset-2",
                    scope === "A"
                      ? "border-emerald-400 bg-gradient-to-br from-emerald-50/90 via-white to-white shadow-sm ring-1 ring-emerald-500/20 dark:from-emerald-950/30 dark:via-card dark:to-card"
                      : "border-border/70 bg-card hover:border-emerald-300 hover:bg-emerald-50/20 hover:shadow-sm dark:hover:border-emerald-800 dark:hover:bg-emerald-950/10"
                  )}
                >
                  <div className="flex w-full items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-3">
                      <input
                        type="radio"
                        name="recommendation-scope"
                        value="A"
                        checked={scope === "A"}
                        onChange={() => setScope("A")}
                        className="sr-only"
                        aria-label="Helhetlig anbefaling"
                      />
                      <span
                        className={cn(
                          "mt-1 flex size-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                          scope === "A"
                            ? "border-emerald-600 bg-emerald-600"
                            : "border-muted-foreground/50 bg-background"
                        )}
                        aria-hidden="true"
                      >
                        {scope === "A" && (
                          <Check
                            className="size-2.5 text-white"
                            strokeWidth={3}
                          />
                        )}
                      </span>
                      <div className="flex min-w-0 items-start gap-3">
                        <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-emerald-100/80 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                          <Layers className="size-6" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-base font-bold leading-tight text-foreground">
                            Helhetlig anbefaling
                          </p>
                          <p className="mt-1 max-w-[280px] text-[11px] leading-relaxed text-muted-foreground">
                            Anbefalingen omfatter hele porteføljen og er
                            tilpasset kundens risikoprofil og investeringsmål.
                          </p>
                        </div>
                      </div>
                    </div>
                    {data.ordersInsideRecommendedRange === true && (
                      <span className="shrink-0 rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">
                        Anbefalt
                      </span>
                    )}
                  </div>
                  {data.equityShareInOrders != null &&
                    data.recommendedMin != null &&
                    data.recommendedMax != null && (
                      <EquityBar
                        value={data.equityShareInOrders}
                        min={data.recommendedMin}
                        max={data.recommendedMax}
                        emphasize={data.ordersInsideRecommendedRange === true}
                        className="mt-4"
                      />
                    )}
                  {scope === "A" && scopeAManuellRequired && (
                    <div className="mt-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[10px] font-medium leading-snug text-amber-700 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
                      Utenfor anbefalt intervall. Legg til en særskilt
                      begrunnelse.
                    </div>
                  )}
                  {scope === "A" && manualSuitabilityRequired && (
                    <div
                      className="mt-2 w-full border-t border-border/40 pt-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Toggle
                        checked={manuellA}
                        onChange={() => {
                          if (manuellA) resetManuellSubFields();
                          setManuellA(!manuellA);
                        }}
                        label="Særskilt begrunnelse"
                      />
                      {manuellA && (
                        <div className="mt-2 space-y-1.5">
                          <p className="text-[10px] text-muted-foreground">
                            Velg hva avviket eller tilpasningen skyldes:
                          </p>
                          {(
                            [
                              [
                                "temporary",
                                "Midlertidig plassering",
                                !isBelowRecommendedRange,
                              ],
                              [
                                "outside",
                                "Utenfor langsiktig allokering",
                                !isBelowRecommendedRange,
                              ],
                              ["pension", "Pensjon", false],
                              ["custom", "Egendefinert", false],
                            ] as const
                          ).map(([key, label, disabled]) => (
                            <Chip
                              key={key}
                              active={manuellSubChoice === key}
                              disabled={disabled}
                              onClick={() =>
                                setManuellSubChoice(
                                  manuellSubChoice === key ? null : key
                                )
                              }
                            >
                              {label}
                            </Chip>
                          ))}
                          {manuellSubChoice === "custom" && (
                            <textarea
                              value={manuellCustomText}
                              onChange={(e) =>
                                setManuellCustomText(e.target.value)
                              }
                              rows={2}
                              placeholder="Beskriv den særskilte begrunnelsen…"
                              className={`${inputCls} resize-y w-full mt-1.5 text-xs`}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Scope B */}
                <div
                  onClick={() => setScope("B")}
                  className={cn(
                    "group relative flex cursor-pointer flex-col rounded-xl border p-5 text-left transition-all duration-200 focus-within:ring-2 focus-within:ring-amber-500 focus-within:ring-offset-2",
                    scope === "B"
                      ? "border-amber-400 bg-gradient-to-br from-amber-50/90 via-white to-white shadow-sm ring-1 ring-amber-500/20 dark:from-amber-950/30 dark:via-card dark:to-card"
                      : "border-border/70 bg-card hover:border-amber-300 hover:bg-amber-50/20 hover:shadow-sm dark:hover:border-amber-800 dark:hover:bg-amber-950/10"
                  )}
                >
                  <div className="flex w-full items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-3">
                      <input
                        type="radio"
                        name="recommendation-scope"
                        value="B"
                        checked={scope === "B"}
                        onChange={() => setScope("B")}
                        className="sr-only"
                        aria-label="Avgrenset anbefaling"
                      />
                      <span
                        className={cn(
                          "mt-1 flex size-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                          scope === "B"
                            ? "border-amber-600 bg-amber-600"
                            : "border-muted-foreground/50 bg-background"
                        )}
                        aria-hidden="true"
                      >
                        {scope === "B" && (
                          <Check
                            className="size-2.5 text-white"
                            strokeWidth={3}
                          />
                        )}
                      </span>
                      <div className="flex min-w-0 items-start gap-3">
                        <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-amber-100/80 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                          <Focus className="size-6" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-base font-bold leading-tight text-foreground">
                            Avgrenset anbefaling
                          </p>
                          <p className="mt-1 max-w-[280px] text-[11px] leading-relaxed text-muted-foreground">
                            Anbefalingen gjelder kun en avgrenset del av
                            porteføljen, for eksempel et produkt eller et
                            bestemt beløp.
                          </p>
                        </div>
                      </div>
                    </div>
                    {data.ordersInsideRecommendedRange === false && (
                      <span className="shrink-0 rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                        Anbefalt
                      </span>
                    )}
                  </div>
                  {data.scopeBEquityShareAfter != null &&
                    data.recommendedMin != null &&
                    data.recommendedMax != null && (
                      <EquityBar
                        value={data.scopeBEquityShareAfter}
                        min={data.recommendedMin}
                        max={data.recommendedMax}
                        emphasize
                        className="mt-4"
                      />
                    )}
                  {scope === "B" && manualSuitabilityRequired && (
                    <div
                      className="mt-2 w-full border-t border-border/40 pt-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Toggle
                        checked={manuellB}
                        onChange={() => {
                          if (manuellB) resetManuellSubFields();
                          setManuellB(!manuellB);
                        }}
                        label="Særskilt begrunnelse"
                      />
                      {manuellB && (
                        <div className="mt-2 space-y-1.5">
                          <p className="text-[10px] text-muted-foreground">
                            Velg hva avviket eller tilpasningen skyldes:
                          </p>
                          {(
                            [
                              [
                                "temporary",
                                "Midlertidig plassering",
                                !isBelowRecommendedRange,
                              ],
                              [
                                "outside",
                                "Utenfor langsiktig allokering",
                                !isBelowRecommendedRange,
                              ],
                              ["pension", "Pensjon", false],
                              ["custom", "Egendefinert", false],
                            ] as const
                          ).map(([key, label, disabled]) => (
                            <Chip
                              key={key}
                              active={manuellSubChoice === key}
                              disabled={disabled}
                              onClick={() =>
                                setManuellSubChoice(
                                  manuellSubChoice === key ? null : key
                                )
                              }
                            >
                              {label}
                            </Chip>
                          ))}
                          {manuellSubChoice === "custom" && (
                            <textarea
                              value={manuellCustomText}
                              onChange={(e) =>
                                setManuellCustomText(e.target.value)
                              }
                              rows={2}
                              placeholder="Beskriv den særskilte begrunnelsen…"
                              className={`${inputCls} resize-y w-full mt-1.5 text-xs`}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {activeManuell &&
                manuellSubChoice &&
                manuellSubChoice !== "custom" && (
                  <div className="mt-3 space-y-2">
                    {manuellSubChoice === "temporary" && (
                      <InfoBanner title="Midlertidig plassering">
                        Midlene beholdes i en forsiktig plassering inntil videre
                        og vurderes løpende som del av ordinær kundeoppfølging.
                      </InfoBanner>
                    )}
                    {manuellSubChoice === "outside" && (
                      <InfoBanner title="Utenfor langsiktig allokering">
                        Anbefalingen gjelder midler som ikke inngår i kundens
                        langsiktige kapitalallokering. Aksjeandel{" "}
                        <strong>{pct(data.equityShareInOrders)}</strong> er
                        egnet for disse midlene.
                      </InfoBanner>
                    )}
                    {manuellSubChoice === "pension" && (
                      <InfoBanner title="Pensjon">
                        Midlene er knyttet til kundens pensjonssparing.
                        Anbefalingens risikoprofil er tilpasset dette formålet
                        og kundens tidshorisont frem til pensjonering.
                      </InfoBanner>
                    )}
                  </div>
                )}
            </section>

            {/* ── Scope B: external investments ──────────────────────────── */}
            {scope === "B" && (
              <section
                className={cn(
                  "rounded-xl border p-3 transition-colors",
                  scopeBRequiresInvestments
                    ? "border-amber-300/80 bg-amber-50/60 dark:border-amber-700/70 dark:bg-amber-950/20"
                    : "border-amber-200/70 bg-amber-50/30 dark:border-amber-800/60 dark:bg-amber-950/10"
                )}
              >
                <div className="mb-2 flex items-start justify-between gap-2.5">
                  <div className="flex min-w-0 items-start gap-2.5">
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                      <Wallet className="size-3.5" aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <h3 className="text-[13px] font-bold leading-tight text-foreground">
                        Finansielle investeringer utenfor Capguard
                      </h3>
                      <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
                        Scope B gjelder investeringer som ikke inngår i
                        Capguard. Registrer beløpene nedenfor for å fullføre
                        vurderingen.
                      </p>
                    </div>
                  </div>
                  {scopeBRequiresInvestments && (
                    <div
                      className="flex max-w-[230px] shrink-0 items-center gap-1.5 rounded-md border border-amber-300 bg-amber-100/70 px-2 py-1 text-amber-950 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100"
                      role="alert"
                    >
                      <AlertTriangle
                        className="size-3.5 shrink-0 text-amber-600 dark:text-amber-400"
                        aria-hidden="true"
                      />
                      <p className="text-[10px] font-semibold leading-snug">
                        Fyll ut minst ett eksternt beløp.
                      </p>
                    </div>
                  )}
                </div>

                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 rounded-lg border border-amber-200/80 bg-background/70 px-2.5 py-1.5 dark:border-amber-800/60 dark:bg-background/40">
                    <label
                      htmlFor="scope-b-investment-date"
                      className="text-xs font-semibold text-foreground"
                    >
                      Dato
                    </label>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <MuiDatePicker
                        value={dayjs(scopeBDate)}
                        onChange={(value) => {
                          if (value?.isValid()) setScopeBDate(value.toDate());
                        }}
                        format="DD.MM.YYYY"
                        slotProps={{
                          textField: {
                            size: "small",
                            inputProps: {
                              "aria-label":
                                "Dato for finansielle investeringer utenfor Capguard",
                            },
                            sx: {
                              width: 145,
                              "& input": { py: 0.25, fontSize: 12 },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </div>
                  <div className="flex items-baseline gap-2 px-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Totalt
                    </span>
                    <span className="text-sm font-bold tabular-nums text-foreground">
                      {scopeBTotal > 0
                        ? `${scopeBTotal.toLocaleString("nb-NO", {
                            maximumFractionDigits: 0,
                          })} kr`
                        : "—"}
                    </span>
                  </div>
                </div>

                <div className="overflow-hidden rounded-lg border border-amber-200/80 bg-background/80 dark:border-amber-800/60 dark:bg-background/40">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/30 border-b border-border">
                        <th className="px-3 py-1.5 text-left text-xs font-semibold text-muted-foreground">
                          Plassering
                        </th>
                        <th className="w-32 px-3 py-1.5 text-right text-xs font-semibold text-muted-foreground">
                          Beløp (NOK)
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/50">
                      {(
                        [
                          [
                            "Aksje- og egenkapitalrelaterte investeringer",
                            scopeBEquity,
                            setScopeBEquity,
                          ],
                          [
                            "Rentebærende investeringer",
                            scopeBInterest,
                            setScopeBInterest,
                          ],
                          [
                            "Kontanter og kortsiktige plasseringer",
                            scopeBCash,
                            setScopeBCash,
                          ],
                          [
                            "Øvrige finansielle investeringer",
                            scopeBOther,
                            setScopeBOther,
                          ],
                        ] as const
                      ).map(([label, value, setter]) => (
                        <tr key={label} className="hover:bg-muted/20">
                          <td className="px-3 py-1 text-sm">{label}</td>
                          <td className="w-32 px-3 py-1">
                            <input
                              type="number"
                              min={0}
                              step={1000}
                              value={value}
                              onChange={(e) =>
                                setter(
                                  e.target.value === ""
                                    ? ""
                                    : Number(e.target.value)
                                )
                              }
                              placeholder="0"
                              className={`${inputCls} w-full text-right py-0.5 border-primary leading-none`}
                              style={{ colorScheme: "normal" }}
                              onFocus={(e) => e.target.select()}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* ── Tilleggsopplysninger + avvikskommentar side by side ────── */}
            <section
              className={cn(
                "flex gap-3",
                data.investmentvsNetWorthFlag ? "items-stretch" : ""
              )}
            >
              <div className="flex-1 flex flex-col">
                <h3 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2.5">
                  Tilleggsopplysninger
                </h3>
                <textarea
                  value={additionalInformationText}
                  onChange={(e) => setAdditionalInformationText(e.target.value)}
                  rows={3}
                  className={`${inputCls} resize-y w-full flex-1`}
                  placeholder="Eventuelle tilleggsopplysninger til rapporten..."
                />
              </div>

              {data.investmentvsNetWorthFlag && (
                <div className="flex-1 flex flex-col rounded-xl border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/20 p-3 space-y-1.5">
                  <p className="text-[10px] font-semibold text-red-700 dark:text-red-400 leading-snug">
                    ⚠ Investering + Kapitalbehov overstiger Likvid formue —
                    kommentar påkrevd.
                  </p>
                  <textarea
                    value={investmentVsNetWorthComment}
                    onChange={(e) =>
                      setInvestmentVsNetWorthComment(e.target.value)
                    }
                    rows={3}
                    placeholder="Forklar avviket..."
                    className={cn(
                      `${inputCls} resize-y w-full flex-1`,
                      !investmentVsNetWorthComment &&
                        "border-red-400 dark:border-red-500"
                    )}
                  />
                </div>
              )}
            </section>

            {/* ── ESG / Bærekraft ─────────────────────────────────────────── */}
            {hasEsgPreference && (
              <section>
                <h3 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2.5">
                  Bærekraftpreferanse
                </h3>
                <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Kunden har oppgitt følgende bærekraftpreferanse:{" "}
                    <span className="font-medium text-foreground">
                      {data.esgPreferenceType ?? data.esgPreference}
                    </span>
                    . De anbefalte investeringene oppfyller denne preferansen
                    ved at{" "}
                    <span
                      className={cn(
                        "font-medium",
                        sustainabilityMatchDescription
                          ? "text-foreground"
                          : "italic text-muted-foreground"
                      )}
                    >
                      {sustainabilityMatchDescription || "[fyll inn nedenfor]"}
                    </span>
                    .
                  </p>
                  <FormField label="Hvordan oppfylles preferansen?">
                    <textarea
                      value={sustainabilityMatchDescription}
                      onChange={(e) =>
                        setSustainabilityMatchDescription(e.target.value)
                      }
                      rows={2}
                      placeholder="f.eks. fondene har SFDR artikkel 8/9 klassifisering..."
                      className={cn(
                        `${inputCls} resize-y w-full`,
                        !sustainabilityMatchDescription.trim() &&
                          "border-red-400 dark:border-red-500"
                      )}
                    />
                  </FormField>
                </div>
              </section>
            )}

            {/* ── §5.7 Bytte / realisasjon ───────────────────────────────── */}
            <section
              aria-labelledby="realisation-heading"
              className={cn(
                "overflow-hidden rounded-xl border transition-colors",
                realisasjon
                  ? "border-amber-300 bg-amber-50/30 dark:border-amber-800 dark:bg-amber-950/10"
                  : "border-border bg-card"
              )}
            >
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2.5">
                <div className="flex min-w-0 items-start gap-2.5">
                  <span
                    className={cn(
                      "mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-lg",
                      realisasjon
                        ? "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300"
                        : "bg-muted text-muted-foreground"
                    )}
                  >
                    <ArrowLeftRight className="size-3" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <h3
                      id="realisation-heading"
                      className="text-[13px] font-semibold leading-tight text-foreground"
                    >
                      Bytte eller realisasjon
                    </h3>
                    <p className="mt-0.5 text-[10px] leading-snug text-muted-foreground">
                      Dokumenter vurderingen når eksisterende investeringer skal
                      erstattes.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 sm:border-l sm:border-border/60 sm:pl-3 dark:sm:border-border/40">
                  <Toggle
                    checked={realisasjon}
                    onChange={() => setRealisasjon(!realisasjon)}
                    label={realisasjon ? "Aktivert" : "Dokumenter bytte"}
                  />
                  {realisasjon && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold tabular-nums text-amber-800 dark:bg-amber-900/50 dark:text-amber-300">
                      {realisasjonCompletedFields}/5
                    </span>
                  )}
                </div>
              </div>

              {realisasjon && (
                <div className="space-y-2.5 border-t border-amber-200/70 bg-background/70 p-3 dark:border-amber-900/60 dark:bg-background/20">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold text-foreground">
                        Dokumentasjon av byttet
                      </p>
                      <p className="mt-0.5 text-[10px] leading-snug text-muted-foreground">
                        Beskriv hva som byttes, hvilke konsekvenser det har, og
                        hvorfor anbefalingen er egnet.
                      </p>
                    </div>
                    <span
                      className={cn(
                        "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums",
                        realisasjonComplete
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300"
                          : "bg-muted text-muted-foreground"
                      )}
                    >
                      {realisasjonComplete
                        ? "Klar"
                        : `${realisasjonCompletedFields} av 5`}
                    </span>
                  </div>

                  <div className="grid gap-2 md:grid-cols-2">
                    {(
                      [
                        [
                          "Eksisterende investering/portefølje",
                          realisasjonEksisterende,
                          setRealisasjonEksisterende,
                          "f.eks. Nordnet-portefølje",
                          false,
                        ],
                        [
                          "Anbefalt løsning",
                          realisasjonAnbefalt,
                          setRealisasjonAnbefalt,
                          "f.eks. Capguard Aktiv 60/40",
                          false,
                        ],
                        [
                          "Kostnader, ulemper og usikkerhet",
                          realisasjonKostnader,
                          setRealisasjonKostnader,
                          "f.eks. realisasjonsgevinst og tegningskostnader",
                          true,
                        ],
                        [
                          "De viktigste forventede fordelene ved byttet",
                          realisasjonFordeler,
                          setRealisasjonFordeler,
                          "f.eks. bedre diversifisering og lavere løpende kostnader",
                          true,
                        ],
                        [
                          "Kort og konkret begrunnelse",
                          realisasjonBegrunnelse,
                          setRealisasjonBegrunnelse,
                          "Forklar hvorfor byttet samlet sett er til kundens beste",
                          true,
                        ],
                      ] as const
                    ).map(
                      (
                        [label, value, setter, placeholder, multiline],
                        index
                      ) => (
                        <div
                          key={label}
                          className={cn(index === 4 && "md:col-span-2")}
                        >
                          <FormField label={label}>
                            {multiline ? (
                              <textarea
                                value={value}
                                onChange={(e) => setter(e.target.value)}
                                placeholder={placeholder}
                                rows={2}
                                className={cn(
                                  inputCls,
                                  "resize-y",
                                  !value && "border-red-400 dark:border-red-500"
                                )}
                              />
                            ) : (
                              <input
                                type="text"
                                value={value}
                                onChange={(e) => setter(e.target.value)}
                                placeholder={placeholder}
                                className={cn(
                                  inputCls,
                                  !value && "border-red-400 dark:border-red-500"
                                )}
                              />
                            )}
                          </FormField>
                        </div>
                      )
                    )}
                  </div>

                  <details className="group rounded-lg border border-border/60 bg-muted/30 text-[11px] text-muted-foreground">
                    <summary className="cursor-pointer select-none px-3 py-1.5 font-medium text-foreground marker:text-muted-foreground">
                      Forhåndsvis dokumenttekst
                    </summary>
                    <div className="border-t border-border/40 px-3 py-2 leading-relaxed">
                      Anbefalingen innebærer at{" "}
                      <span
                        className={cn(
                          "font-medium",
                          realisasjonEksisterende ? "text-foreground" : "italic"
                        )}
                      >
                        {realisasjonEksisterende ||
                          "eksisterende investering eller portefølje"}
                      </span>{" "}
                      helt eller delvis realiseres og erstattes med{" "}
                      <span
                        className={cn(
                          "font-medium",
                          realisasjonAnbefalt ? "text-foreground" : "italic"
                        )}
                      >
                        {realisasjonAnbefalt || "anbefalt løsning"}
                      </span>
                      . Det er lagt vekt på{" "}
                      <span
                        className={cn(
                          "font-medium",
                          realisasjonKostnader ? "text-foreground" : "italic"
                        )}
                      >
                        {realisasjonKostnader || "kostnader og ulemper"}
                      </span>
                      , sammenholdt med{" "}
                      <span
                        className={cn(
                          "font-medium",
                          realisasjonFordeler ? "text-foreground" : "italic"
                        )}
                      >
                        {realisasjonFordeler || "forventede fordeler"}
                      </span>
                      . Fordelene vurderes å overstige kostnadene fordi{" "}
                      <span
                        className={cn(
                          "font-medium",
                          realisasjonBegrunnelse ? "text-foreground" : "italic"
                        )}
                      >
                        {realisasjonBegrunnelse || "begrunnelse"}
                      </span>
                      .
                    </div>
                  </details>
                </div>
              )}
            </section>

            {/* ── Actions ────────────────────────────────────────────────── */}
            <div className="flex items-center justify-between pt-2 border-t border-border/40">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleDownloadReportPriv}
                  disabled={
                    downloadingPriv ||
                    !hasOrders ||
                    !realisasjonComplete ||
                    !investmentVsNetWorthComplete ||
                    !manuellCustomComplete ||
                    !sustainabilityComplete ||
                    scopeAManuellRequired ||
                    scopeBRequiresInvestments
                  }
                  className="px-3.5 py-2 rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300 text-xs font-semibold hover:bg-violet-100 dark:hover:bg-violet-900/30 disabled:opacity-50 transition-colors"
                >
                  {downloadingPriv ? "Genererer…" : "PRIV"}
                </button>
                <button
                  type="button"
                  onClick={handleDownloadReport}
                  disabled={
                    downloading ||
                    !hasOrders ||
                    !realisasjonComplete ||
                    !investmentVsNetWorthComplete ||
                    !manuellCustomComplete ||
                    !sustainabilityComplete ||
                    scopeAManuellRequired ||
                    scopeBRequiresInvestments
                  }
                  className="px-3.5 py-2 rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300 text-xs font-semibold hover:bg-violet-100 dark:hover:bg-violet-900/30 disabled:opacity-50 transition-colors"
                >
                  {downloading ? "Genererer…" : "ORG"}
                </button>
              </div>
              <div className="flex gap-2">
                {onClose && (
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors"
                  >
                    Avbryt
                  </button>
                )}
                <Button
                  type="submit"
                  variant="default"
                  size="md"
                  disabled={
                    !hasOrders ||
                    !realisasjonComplete ||
                    !investmentVsNetWorthComplete ||
                    !manuellCustomComplete ||
                    !sustainabilityComplete ||
                    scopeAManuellRequired ||
                    scopeBRequiresInvestments
                  }
                >
                  Legg til dokument
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Side panel ──────────────────────────────────────────────────── */}
      <aside className="w-96 shrink-0 space-y-4 sticky top-0">
        {/* Equity overview */}
        {data.equityShareInOrders != null && (
          <div className="rounded-xl border border-border bg-card p-3 space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Aksjeandel
            </p>
            <div className="space-y-1.5">
              <PanelRow
                label="Ny investering"
                value={pct(data.equityShareInOrders)}
                highlight
              />
              {data.recommendedEquityShareText && (
                <PanelRow
                  label="Anbefalt intervall"
                  value={data.recommendedEquityShareText}
                />
              )}
              {data.equityShareBefore != null && (
                <PanelRow
                  label="Nåværende"
                  value={pct(data.equityShareBefore)}
                />
              )}
            </div>
          </div>
        )}

        {/* Financial situation */}
        <div className="rounded-xl border border-border bg-card p-3 space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Økonomi
          </p>
          <div className="space-y-1.5">
            <PanelRow
              label="Investering"
              value={fmtNok(data.investmentAmount)}
            />
            <PanelRow
              label="Kapitalbehov"
              value={fmtNok(data.liquidityBuffer)}
            />
            <PanelRow
              label="Likvid formue"
              value={fmtNok(data.liquidInvestableWealth)}
            />
          </div>
          {data.investmentvsNetWorthFlag && (
            <div className="mt-1 rounded-md bg-red-100 dark:bg-red-900/40 border border-red-200 dark:border-red-800 px-2 py-1.5 text-[10px] font-medium text-red-700 dark:text-red-300 leading-snug">
              Investering + Kapitalbehov overstiger Likvid formue
            </div>
          )}
        </div>

        {/* All output variables — full dump of everything sent to the document */}
        <div className="rounded-xl border border-border bg-card p-3 space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Alle outputvariabler
          </p>
          <table className="w-full text-[10px] border-collapse">
            <tbody>
              {Object.entries(finalData).map(([key, value]) => {
                if (value === null || value === undefined || value === "")
                  return null;
                const display =
                  typeof value === "boolean"
                    ? value
                      ? "Ja"
                      : "Nei"
                    : typeof value === "object"
                    ? JSON.stringify(value)
                    : String(value);
                return (
                  <tr
                    key={key}
                    className="border-b border-border/50 last:border-0"
                  >
                    <td className="py-0.5 pr-2 text-muted-foreground align-top whitespace-nowrap">
                      {key}
                    </td>
                    <td className="py-0.5 font-medium text-right break-all">
                      {display}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </aside>
    </form>
  );
}

// ── Small reusable display components ───────────────────────────────────────

const SUITABILITY_STATUS_META: Record<
  string,
  { label: string; className: string }
> = {
  AUTO_OK: {
    label: "Automatisk godkjent",
    className:
      "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300",
  },
  MANUAL_SUITABILITY_REQUIRED: {
    label: "Krever manuell vurdering",
    className:
      "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
  },
  INPUT_INCOMPLETE: {
    label: "Mangler inndata",
    className:
      "border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300",
  },
  BLOCKED_BY_REVIEW: {
    label: "Blokkert av kontroll",
    className:
      "border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300",
  },
  NOT_REQUESTED: {
    label: "Ikke vurdert",
    className: "border-border bg-muted text-muted-foreground",
  },
};

function SuitabilityStatusBadge({
  status,
}: {
  status: string | null | undefined;
}) {
  const meta =
    (status ? SUITABILITY_STATUS_META[status] : undefined) ??
    SUITABILITY_STATUS_META.NOT_REQUESTED;
  return (
    <span
      title={status ?? "NOT_REQUESTED"}
      className={cn(
        "shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold",
        meta.className
      )}
    >
      {meta.label}
    </span>
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={onChange}
        className={cn(
          "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          checked ? "bg-blue-600" : "bg-gray-200 dark:bg-gray-700"
        )}
      >
        <span
          className={cn(
            "pointer-events-none inline-block size-4 rounded-full bg-white shadow-lg transition-transform",
            checked ? "translate-x-4" : "translate-x-0"
          )}
        />
      </button>
      {/* Label click is auto-forwarded to the button by the wrapping <label> */}
      <span className="text-xs text-muted-foreground">{label}</span>
    </label>
  );
}

function Chip({
  active,
  onClick,
  disabled = false,
  children,
}: {
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={cn(
        "rounded-full border px-2.5 py-1 text-[11px] font-medium transition-all",
        disabled
          ? "cursor-not-allowed border-border/40 text-muted-foreground/40"
          : active
          ? "border-blue-400 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300"
          : "border-border/60 text-muted-foreground hover:border-blue-300 hover:text-foreground"
      )}
    >
      {children}
    </button>
  );
}

function InfoBanner({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20 px-3.5 py-3 text-xs text-blue-900 dark:text-blue-100 leading-relaxed">
      <span className="font-semibold">{title}: </span>
      {children}
    </div>
  );
}

function PanelRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-1">
      <span className="text-[10px] text-muted-foreground truncate">
        {label}
      </span>
      <span
        className={cn(
          "text-[11px] tabular-nums font-semibold shrink-0",
          highlight && "text-blue-700 dark:text-blue-300"
        )}
      >
        {value}
      </span>
    </div>
  );
}

function EquityBar({
  value,
  min,
  max,
  emphasize = true,
  className,
}: {
  value: number;
  min: number;
  max: number;
  emphasize?: boolean;
  className?: string;
}) {
  const clampPct = (fraction: number) =>
    Math.min(100, Math.max(0, fraction * 100));
  const valuePct = clampPct(value);
  const minPct = clampPct(Math.min(min, max));
  const maxPct = clampPct(Math.max(min, max));
  const displayValue = Math.round(valuePct);
  const displayMin = Math.round(minPct);
  const displayMax = Math.round(maxPct);
  const isInside = valuePct >= minPct && valuePct <= maxPct;
  const isBelow = valuePct < minPct;
  const status = isInside ? "Innenfor" : isBelow ? "Under" : "Over";
  const accent = isInside
    ? {
        dot: "bg-emerald-500",
        pill: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300",
      }
    : isBelow
    ? {
        dot: "bg-sky-500",
        pill: "bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300",
      }
    : {
        dot: "bg-amber-500",
        pill: "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300",
      };

  return (
    <div
      className={cn("w-full", className)}
      role="img"
      aria-label={`Aksjeandel ${displayValue} prosent. Anbefalt intervall er ${displayMin} til ${displayMax} prosent. ${status} anbefalt intervall.`}
    >
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
            Anbefalt aksjeandel
          </p>
          <div className="mt-1 flex items-baseline gap-1">
            <span className="text-lg font-bold leading-none tabular-nums text-foreground">
              {displayValue} %
            </span>
            <span className="text-[10px] font-medium text-muted-foreground">
              aksjer
            </span>
          </div>
        </div>
        {emphasize ? (
          <span
            className={cn(
              "inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[10px] font-semibold leading-tight",
              accent.pill
            )}
          >
            <span className="text-right">
              {status === "Innenfor"
                ? "Innenfor anbefalt"
                : `${status} anbefalt`}
              <br />
              intervall
            </span>
            {isInside && (
              <span
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center rounded-full text-white",
                  accent.dot
                )}
              >
                <Check className="size-2.5" strokeWidth={3} />
              </span>
            )}
          </span>
        ) : (
          <span className="shrink-0 rounded-lg bg-slate-100 px-2.5 py-1.5 text-right text-[10px] font-semibold leading-tight text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
            {status === "Innenfor" ? "Innenfor anbefalt" : `${status} anbefalt`}
            <br />
            intervall
          </span>
        )}
      </div>

      <div className="relative mt-3 h-4" aria-hidden="true">
        <div className="absolute inset-x-0 top-1/2 h-2 -translate-y-1/2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800/60">
          <div
            className="absolute inset-y-0 rounded-full bg-emerald-300/80 dark:bg-emerald-700/60"
            style={{
              left: `${minPct}%`,
              width: `${Math.max(maxPct - minPct, 1)}%`,
            }}
          />
        </div>
        <div
          className={cn(
            "absolute top-1/2 size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-sm ring-1 ring-black/10 transition-[left] duration-300 dark:border-slate-950 dark:ring-white/20",
            accent.dot
          )}
          style={{ left: `${valuePct}%` }}
        />
      </div>

      <div className="relative mt-1 h-3 text-[8px] font-medium tabular-nums text-muted-foreground/80">
        <span className="absolute left-0">0 %</span>
        {minPct > 3 && (
          <span
            className="absolute -translate-x-1/2"
            style={{ left: `${minPct}%` }}
          >
            {displayMin} %
          </span>
        )}
        {maxPct < 97 && (
          <span
            className="absolute -translate-x-1/2"
            style={{ left: `${maxPct}%` }}
          >
            {displayMax} %
          </span>
        )}
        <span className="absolute right-0">100 %</span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[9px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className={cn("size-2 rounded-full", accent.dot)} />
          Din aksjeandel ({displayValue} %)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-4 rounded-full bg-emerald-300/80 dark:bg-emerald-700/60" />
          Anbefalt intervall ({displayMin}–{displayMax} %)
        </span>
      </div>
    </div>
  );
}
